// ============================================================================
// FICHIER : src/pages/admin-saas/UtilisateursVigiePage.tsx
// DESCRIPTION : Gestion des utilisateurs VIGIE — Admin SaaS
// AUTEUR : Claude (Session 148, modifié Session 149, Session 163)
// DATE : 24/02/2026
// Session 163 : Ajout reset mot de passe (via Mailjet) + suppression compte
// Session 280 : Recharge quota SENTINELLE (sentinelleUsage → 0 sur coproprietes/vigie-{uid})
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  doc, 
  getDoc,
  updateDoc,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { 
  Users, 
  Star, 
  Search,
  RefreshCw,
  Shield,
  ShieldOff,
  Eye,
  X,
  CheckCircle2,
  XCircle,
  Clock,
  CreditCard,
  Calendar,
  FileSearch,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Loader2,
  UserCheck,
  UserX,
  Mail,
  Building2,
  Activity,
  ExternalLink,
  KeyRound,
  Trash2,
  BarChart2
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface VigieUser {
  id: string; // uid Firebase
  nom: string;
  prenom: string;
  email: string;
  role: string;
  status: string; // 'active', 'inactive', 'suspended'
  createdAt: Date | null;
  lastLogin: Date | null;
  // Stripe
  typeOffre?: string; // 'fondateur', 'normal'
  statutAbonnement?: string; // 'actif', 'annule', 'en_attente_paiement', 'expire'
  formuleAbonnement?: string; // 'mensuel', 'annuel'
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  // VIGIE info
  vigieInfo?: {
    nomCopropriete?: string;
    adresse?: string;
    nomSyndic?: string;
    nombreLots?: number;
  };
  // Activité
  nbAnalyses?: number;
  // Quota SENTINELLE
  sentinelleUsage?: number;
  sentinelleQuota?: number;
}

type FilterStatut = 'tous' | 'active' | 'inactive' | 'suspended';
type FilterOffre = 'tous' | 'fondateur' | 'normal' | 'sans_abonnement';

// ============================================
// HELPERS
// ============================================

const formatDate = (date: Date | null): string => {
  if (!date) return 'Non renseigné';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateHeure = (date: Date | null): string => {
  if (!date) return 'Jamais';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getStatutAbonnementBadge = (user: VigieUser) => {
  const statut = user.statutAbonnement;
  if (!statut) {
    return { label: 'Sans abonnement', bg: 'bg-gray-100', text: 'text-gray-600' };
  }
  switch (statut) {
    case 'actif':
      return { label: 'Actif', bg: 'bg-emerald-100', text: 'text-emerald-700' };
    case 'annule':
      return { label: 'Annulé', bg: 'bg-red-100', text: 'text-red-700' };
    case 'en_attente_paiement':
      return { label: 'En attente', bg: 'bg-amber-100', text: 'text-amber-700' };
    case 'expire':
      return { label: 'Expiré', bg: 'bg-gray-100', text: 'text-gray-600' };
    default:
      return { label: statut, bg: 'bg-gray-100', text: 'text-gray-600' };
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'active':
      return { label: 'Actif', bg: 'bg-emerald-100', text: 'text-emerald-700', icon: UserCheck };
    case 'inactive':
      return { label: 'Inactif', bg: 'bg-gray-100', text: 'text-gray-600', icon: UserX };
    case 'suspended':
      return { label: 'Suspendu', bg: 'bg-red-100', text: 'text-red-700', icon: ShieldOff };
    default:
      return { label: status, bg: 'bg-gray-100', text: 'text-gray-600', icon: UserX };
  }
};

const getOffreBadge = (typeOffre?: string) => {
  if (!typeOffre) return null;
  if (typeOffre === 'fondateur') {
    return { label: 'Fondateur', bg: 'bg-amber-100', text: 'text-amber-700', icon: Star };
  }
  return { label: 'Normal', bg: 'bg-blue-100', text: 'text-blue-700', icon: CreditCard };
};

// ============================================
// COMPOSANT
// ============================================

const UtilisateursVigiePage: React.FC = () => {
  const [users, setUsers] = useState<VigieUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState<FilterStatut>('tous');
  const [filterOffre, setFilterOffre] = useState<FilterOffre>('tous');

  // Détail user
  const [selectedUser, setSelectedUser] = useState<VigieUser | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Action en cours
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Stats
  const [statsPlacesFondateur, setStatsPlacesFondateur] = useState(0);

  // ==========================================
  // CHARGEMENT DES DONNÉES
  // ==========================================

  const loadUsers = async () => {
    setError(null);
    try {
      // Requêter tous les users VIGIE
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'vigie')
      );
      const snapshot = await getDocs(q);

      const usersList: VigieUser[] = [];
      let fondateursCount = 0;

      for (const userDoc of snapshot.docs) {
        const data = userDoc.data();

        // Compter les analyses SENTINELLE (collection quotas ou logs)
        let nbAnalyses = 0;
        try {
          const quotaRef = doc(db, `coproprietes/${data.coproprieteIds?.[0]}/quotas/sentinelle`);
          // On pourrait lire les quotas ici, mais pour simplifier on utilise
          // un champ directement sur le user s'il existe
          nbAnalyses = data.nbAnalyses || 0;
        } catch {
          // Silencieux
        }

        const user: VigieUser = {
          id: userDoc.id,
          nom: data.nom || '',
          prenom: data.prenom || '',
          email: data.email || '',
          role: data.role || 'vigie',
          status: data.status || 'active',
          createdAt: data.createdAt?.toDate() || null,
          lastLogin: data.lastLogin?.toDate() || null,
          typeOffre: data.typeOffre,
          statutAbonnement: data.statutAbonnement,
          formuleAbonnement: data.formuleAbonnement,
          stripeCustomerId: data.stripeCustomerId,
          stripeSubscriptionId: data.stripeSubscriptionId,
          vigieInfo: data.vigieInfo,
          nbAnalyses
        };

        // Charger quota depuis coproprietes/vigie-{uid}
        try {
          const coproprieteRef = doc(db, 'coproprietes', `vigie-${userDoc.id}`);
          const coproprieteSnap = await getDoc(coproprieteRef);
          if (coproprieteSnap.exists()) {
            const coproprieteData = coproprieteSnap.data();
            user.sentinelleUsage = coproprieteData.sentinelleUsage ?? 0;
            user.sentinelleQuota = coproprieteData.sentinelleQuota ?? 100;
          }
        } catch {
          // Silencieux — quota non disponible
        }

        if (data.typeOffre === 'fondateur' && 
            ['actif', 'en_attente_paiement'].includes(data.statutAbonnement || '')) {
          fondateursCount++;
        }

        usersList.push(user);
      }

      // Trier par date de création (plus récent en premier)
      usersList.sort((a, b) => {
        const dateA = a.createdAt?.getTime() || 0;
        const dateB = b.createdAt?.getTime() || 0;
        return dateB - dateA;
      });

      setUsers(usersList);
      setStatsPlacesFondateur(fondateursCount);
    } catch (err: any) {
      console.error('Erreur chargement utilisateurs VIGIE:', err);
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    loadUsers();
  };

  // ==========================================
  // ACTIONS
  // ==========================================

  const handleToggleStatus = async (user: VigieUser, newStatus: 'active' | 'suspended') => {
    if (!window.confirm(
      newStatus === 'suspended' 
        ? `Suspendre le compte de ${user.prenom} ${user.nom} ? L'utilisateur ne pourra plus se connecter.`
        : `Réactiver le compte de ${user.prenom} ${user.nom} ?`
    )) return;

    setActionLoading(user.id);
    try {
      await updateDoc(doc(db, 'users', user.id), {
        status: newStatus,
        updatedAt: Timestamp.now()
      });

      // Mettre à jour localement
      setUsers(prev => prev.map(u => 
        u.id === user.id ? { ...u, status: newStatus } : u
      ));

      // Si le détail est ouvert, mettre à jour
      if (selectedUser?.id === user.id) {
        setSelectedUser(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch (err: any) {
      console.error('Erreur changement statut:', err);
      alert(`Erreur : ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================
  // Session 163 : Réinitialiser le mot de passe
  // ==========================================

  const handleResetPassword = async (user: VigieUser) => {
    if (!window.confirm(
      `Envoyer un email de réinitialisation de mot de passe à ${user.prenom} ${user.nom} (${user.email}) ?`
    )) return;

    setActionLoading(user.id);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const resetPassword = httpsCallable<
        { email: string; adminMode: boolean },
        { success: boolean }
      >(functions, 'resetUserPassword');

      await resetPassword({ email: user.email, adminMode: true });
      alert(`Email de réinitialisation envoyé à ${user.email}`);
    } catch (err: any) {
      console.error('Erreur reset password:', err);
      alert(`Erreur : ${err.message || 'Impossible d\'envoyer l\'email'}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================
  // Session 163 : Supprimer un compte complet
  // ==========================================

  const handleDeleteAccount = async (user: VigieUser) => {
    const confirmation = window.prompt(
      `ATTENTION : Suppression définitive du compte de ${user.prenom} ${user.nom} (${user.email}).\n\n` +
      `Cette action supprime :\n` +
      `- Le compte de connexion\n` +
      `- Tous les documents, courriers, signalements\n` +
      `- Les quotas et la copropriété\n\n` +
      `Tapez SUPPRIMER pour confirmer :`
    );

    if (confirmation !== 'SUPPRIMER') return;

    setActionLoading(user.id);
    try {
      const functions = getFunctions(undefined, 'europe-west1');
      const deleteAccount = httpsCallable<
        { uid: string },
        { success: boolean; deleted: any }
      >(functions, 'deleteUserAccount');

      await deleteAccount({ uid: user.id });

      // Retirer de la liste locale
      setUsers(prev => prev.filter(u => u.id !== user.id));
      setShowDetail(false);
      setSelectedUser(null);

      alert(`Compte de ${user.prenom} ${user.nom} supprimé définitivement.`);
    } catch (err: any) {
      console.error('Erreur suppression compte:', err);
      alert(`Erreur : ${err.message || 'Impossible de supprimer le compte'}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================
  // Recharger quota SENTINELLE
  // ==========================================

  const handleRechargerQuota = async (user: VigieUser) => {
    if (!window.confirm(
      `Recharger le quota SENTINELLE de ${user.prenom} ${user.nom} ?

` +
      `Consommation actuelle : ${user.sentinelleUsage ?? '?'}/${user.sentinelleQuota ?? 100} analyses.

` +
      `Le compteur sentinelleUsage sera remis à 0.`
    )) return;

    setActionLoading(`quota-${user.id}`);
    try {
      const coproprieteRef = doc(db, 'coproprietes', `vigie-${user.id}`);
      await updateDoc(coproprieteRef, {
        sentinelleUsage: 0,
        updatedAt: Timestamp.now()
      });

      // Mettre à jour localement
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, sentinelleUsage: 0 } : u
      ));
      if (selectedUser?.id === user.id) {
        setSelectedUser(prev => prev ? { ...prev, sentinelleUsage: 0 } : null);
      }

      alert(`Quota de ${user.prenom} ${user.nom} remis à zéro.`);
    } catch (err: any) {
      console.error('Erreur recharge quota:', err);
      alert(`Erreur : ${err.message || 'Impossible de recharger le quota'}`);
    } finally {
      setActionLoading(null);
    }
  };

  // ==========================================
  // FILTRAGE
  // ==========================================

  const filteredUsers = users.filter(user => {
    // Recherche texte
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const match = 
        user.nom.toLowerCase().includes(search) ||
        user.prenom.toLowerCase().includes(search) ||
        user.email.toLowerCase().includes(search) ||
        (user.vigieInfo?.nomCopropriete || '').toLowerCase().includes(search);
      if (!match) return false;
    }

    // Filtre statut compte
    if (filterStatut !== 'tous' && user.status !== filterStatut) return false;

    // Filtre type offre
    if (filterOffre === 'fondateur' && user.typeOffre !== 'fondateur') return false;
    if (filterOffre === 'normal' && user.typeOffre !== 'normal') return false;
    if (filterOffre === 'sans_abonnement' && user.typeOffre) return false;

    return true;
  });

  // ==========================================
  // STATS CALCULÉES
  // ==========================================

  const statsActifs = users.filter(u => u.status === 'active').length;
  const statsSuspendus = users.filter(u => u.status === 'suspended').length;
  const statsAbonnes = users.filter(u => u.statutAbonnement === 'actif').length;

  // ==========================================
  // RENDU
  // ==========================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <XCircle className="mx-auto text-red-500 mb-3" size={48} />
        <h2 className="text-lg font-semibold text-red-800 mb-2">Erreur de chargement</h2>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Utilisateurs VIGIE</h1>
          <p className="text-gray-500 mt-1">
            {users.length} utilisateur{users.length > 1 ? 's' : ''} enregistré{users.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Cartes statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Total users */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              <p className="text-sm text-gray-500">Total</p>
            </div>
          </div>
        </div>

        {/* Actifs */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
              <UserCheck className="text-emerald-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{statsActifs}</p>
              <p className="text-sm text-gray-500">Actifs</p>
            </div>
          </div>
        </div>

        {/* Abonnés Stripe */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <CreditCard className="text-purple-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{statsAbonnes}</p>
              <p className="text-sm text-gray-500">Abonnés</p>
            </div>
          </div>
        </div>

        {/* Places fondateurs */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-amber-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Star className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {statsPlacesFondateur}<span className="text-base font-normal text-gray-400">/20</span>
              </p>
              <p className="text-sm text-gray-500">Fondateurs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Recherche */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher par nom, email, copropriété..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Filtre statut */}
          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value as FilterStatut)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-sm"
          >
            <option value="tous">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="suspended">Suspendus</option>
            <option value="inactive">Inactifs</option>
          </select>

          {/* Filtre offre */}
          <select
            value={filterOffre}
            onChange={(e) => setFilterOffre(e.target.value as FilterOffre)}
            className="px-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white text-sm"
          >
            <option value="tous">Toutes les offres</option>
            <option value="fondateur">Fondateurs</option>
            <option value="normal">Normal</option>
            <option value="sans_abonnement">Sans abonnement</option>
          </select>
        </div>

        {/* Compteur résultats filtrés */}
        {(searchTerm || filterStatut !== 'tous' || filterOffre !== 'tous') && (
          <p className="text-sm text-gray-500 mt-3">
            {filteredUsers.length} résultat{filteredUsers.length > 1 ? 's' : ''}
            {searchTerm && ` pour "${searchTerm}"`}
          </p>
        )}
      </div>

      {/* Tableau des utilisateurs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="mx-auto text-gray-300 mb-3" size={48} />
            <p className="text-gray-500">
              {users.length === 0 
                ? 'Aucun utilisateur VIGIE enregistré' 
                : 'Aucun résultat pour ces critères'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                    Utilisateur
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Compte
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Offre
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase">
                    Abonnement
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">
                    Inscription
                  </th>
                  <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">
                    Dernière connexion
                  </th>
                  <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => {
                  const statusBadge = getStatusBadge(user.status);
                  const StatusIcon = statusBadge.icon;
                  const aboBadge = getStatutAbonnementBadge(user);
                  const offreBadge = getOffreBadge(user.typeOffre);

                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      {/* Utilisateur */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-700 font-semibold text-sm">
                              {user.prenom.charAt(0)}{user.nom.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">
                              {user.prenom} {user.nom}
                            </p>
                            <p className="text-sm text-gray-500 truncate">{user.email}</p>
                            {user.vigieInfo?.nomCopropriete && (
                              <p className="text-xs text-gray-400 truncate">
                                {user.vigieInfo.nomCopropriete}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Statut compte */}
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                          <StatusIcon size={12} />
                          {statusBadge.label}
                        </span>
                      </td>

                      {/* Type offre */}
                      <td className="px-5 py-4 text-center">
                        {offreBadge ? (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${offreBadge.bg} ${offreBadge.text}`}>
                            <offreBadge.icon size={12} />
                            {offreBadge.label}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>

                      {/* Abonnement */}
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${aboBadge.bg} ${aboBadge.text}`}>
                          {aboBadge.label}
                        </span>
                        {user.formuleAbonnement && (
                          <p className="text-xs text-gray-400 mt-1">{user.formuleAbonnement}</p>
                        )}
                      </td>

                      {/* Date inscription */}
                      <td className="px-5 py-4 text-center text-sm text-gray-600 hidden lg:table-cell">
                        {formatDate(user.createdAt)}
                      </td>

                      {/* Dernière connexion */}
                      <td className="px-5 py-4 text-center text-sm hidden lg:table-cell">
                        <span className={user.lastLogin ? 'text-gray-600' : 'text-gray-400'}>
                          {formatDateHeure(user.lastLogin)}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Voir détail */}
                          <button
                            onClick={() => { setSelectedUser(user); setShowDetail(true); }}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Voir le détail"
                          >
                            <Eye size={16} />
                          </button>

                          {/* Reset mot de passe */}
                          <button
                            onClick={() => handleResetPassword(user)}
                            disabled={actionLoading === user.id}
                            className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Réinitialiser le mot de passe"
                          >
                            <KeyRound size={16} />
                          </button>

                          {/* Activer / Suspendre */}
                          {actionLoading === user.id ? (
                            <Loader2 size={16} className="animate-spin text-gray-400" />
                          ) : user.status === 'active' ? (
                            <button
                              onClick={() => handleToggleStatus(user, 'suspended')}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Suspendre le compte"
                            >
                              <ShieldOff size={16} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleStatus(user, 'active')}
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Réactiver le compte"
                            >
                              <Shield size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal détail utilisateur */}
      {showDetail && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-bold">
                      {selectedUser.prenom.charAt(0)}{selectedUser.nom.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      {selectedUser.prenom} {selectedUser.nom}
                    </h2>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Sections */}
              <div className="space-y-4">
                {/* Statut du compte */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Compte
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Statut :</span>
                      <span className={`ml-2 font-medium ${
                        selectedUser.status === 'active' ? 'text-emerald-600' : 
                        selectedUser.status === 'suspended' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {getStatusBadge(selectedUser.status).label}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Rôle :</span>
                      <span className="ml-2 font-medium text-gray-900">VIGIE</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Inscrit le :</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatDate(selectedUser.createdAt)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Dernière connexion :</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {formatDateHeure(selectedUser.lastLogin)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Copropriété */}
                {selectedUser.vigieInfo && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                      Copropriété
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Building2 size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-900">
                          {selectedUser.vigieInfo.nomCopropriete || 'Non renseigné'}
                        </span>
                      </div>
                      {selectedUser.vigieInfo.adresse && (
                        <div className="flex items-start gap-2">
                          <Mail size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{selectedUser.vigieInfo.adresse}</span>
                        </div>
                      )}
                      {selectedUser.vigieInfo.nomSyndic && (
                        <div className="flex items-start gap-2">
                          <Shield size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">Syndic : {selectedUser.vigieInfo.nomSyndic}</span>
                        </div>
                      )}
                      {selectedUser.vigieInfo.nombreLots && (
                        <div className="flex items-start gap-2">
                          <Building2 size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{selectedUser.vigieInfo.nombreLots} lots</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Abonnement */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Abonnement
                  </h3>
                  {selectedUser.statutAbonnement ? (
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Statut :</span>
                        <span className={`ml-2 font-medium ${
                          selectedUser.statutAbonnement === 'actif' ? 'text-emerald-600' : 'text-gray-600'
                        }`}>
                          {getStatutAbonnementBadge(selectedUser).label}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Type :</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {selectedUser.typeOffre === 'fondateur' ? 'Fondateur' : 'Normal'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Formule :</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {selectedUser.formuleAbonnement || 'Non renseigné'}
                        </span>
                      </div>
                      {selectedUser.stripeCustomerId && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Stripe ID :</span>
                          <span className="ml-2 font-mono text-xs text-gray-600">
                            {selectedUser.stripeCustomerId}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <AlertTriangle size={16} className="text-amber-500" />
                      <span>Aucun abonnement Stripe — utilisateur pré-inscrit</span>
                    </div>
                  )}
                </div>

                {/* UID technique */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Technique
                  </h3>
                  <div className="text-sm">
                    <span className="text-gray-500">UID :</span>
                    <span className="ml-2 font-mono text-xs text-gray-600 break-all">
                      {selectedUser.id}
                    </span>
                  </div>
                  {selectedUser.vigieInfo && (
                    <div className="text-sm mt-2">
                      <span className="text-gray-500">Copropriété ID :</span>
                      <span className="ml-2 font-mono text-xs text-gray-600 break-all">
                        vigie-{selectedUser.id}
                      </span>
                    </div>
                  )}
                  <div className="text-sm mt-2">
                    <span className="text-gray-500">Quota SENTINELLE :</span>
                    <span className={`ml-2 font-medium ${
                      (selectedUser.sentinelleUsage ?? 0) >= (selectedUser.sentinelleQuota ?? 100)
                        ? 'text-red-600' : 'text-gray-900'
                    }`}>
                      {selectedUser.sentinelleUsage ?? 0} / {selectedUser.sentinelleQuota ?? 100} analyses utilisées
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
                {/* Ligne 1 : Voir espace + Reset MDP */}
                <div className="flex flex-wrap gap-3">
                  <Link
                    to={`/vigie/dashboard?copro=vigie-${selectedUser.id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <ExternalLink size={16} />
                    <span>Voir son espace VIGIE</span>
                  </Link>

                  <button
                    onClick={() => handleResetPassword(selectedUser)}
                    disabled={actionLoading === selectedUser.id}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === selectedUser.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <KeyRound size={16} />
                    )}
                    <span>Réinitialiser MDP</span>
                  </button>

                  <button
                    onClick={() => handleRechargerQuota(selectedUser)}
                    disabled={actionLoading === `quota-${selectedUser.id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === `quota-${selectedUser.id}` ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <BarChart2 size={16} />
                    )}
                    <span>Recharger quota</span>
                  </button>
                </div>

                {/* Ligne 2 : Suspendre/Réactiver + Supprimer */}
                <div className="flex flex-wrap gap-3">
                  {selectedUser.status === 'active' ? (
                    <button
                      onClick={() => handleToggleStatus(selectedUser, 'suspended')}
                      disabled={actionLoading === selectedUser.id}
                      className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === selectedUser.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <ShieldOff size={16} />
                      )}
                      <span>Suspendre</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleStatus(selectedUser, 'active')}
                      disabled={actionLoading === selectedUser.id}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
                    >
                      {actionLoading === selectedUser.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Shield size={16} />
                      )}
                      <span>Réactiver</span>
                    </button>
                  )}

                  <button
                    onClick={() => handleDeleteAccount(selectedUser)}
                    disabled={actionLoading === selectedUser.id}
                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-300 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    {actionLoading === selectedUser.id ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                    <span>Supprimer le compte</span>
                  </button>
                </div>

                {/* Fermer */}
                <button
                  onClick={() => setShowDetail(false)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtilisateursVigiePage;
